/* eslint-disable */
// import React, { useEffect } from "react";
import { BehaviorSubject, Observable } from "rxjs";
import {
  IComponentContext,
  UpdateCommand,
  ComponentContextReadyState,
  DataflowIntegration,
  SingleValueParameter,
  MultiValueParameter,
} from "@sa/rep-core";
import { dataService } from "../../dataFlow";
import { OLAPResult } from "@sa/rep-olap";
import { SelectionMode, AggregationType } from "@sa/rep-common";
import { ComponentContextCreateOptionsBasf, ServicesRegister } from "../../shared";
import { debounceTime } from "rxjs/operators";
import { arrayByLength, clone, equal } from "../../../../shared";
import { SLIDE_NUMBER } from "./constants";
import { DictionariesService, DictionaryItem } from "@sa/rep-mdm";
import { getDefaultMark } from './utils/getDefaultMark';

type Parameters = {
  commodities: number[];
  calendar: number[];
  baseIndex: number;
  method: number;
};

export class SlideContext implements IComponentContext {
  public id: string;
  public readonly options: ComponentContextCreateOptionsBasf;
  public readonly dataflowIntegration: DataflowIntegration;
  public readyState: ComponentContextReadyState;

  public olapResult$ = new BehaviorSubject<OLAPResult | null>(null);
  public sources$ = new BehaviorSubject<string | null>(null);

  protected _parameters: BehaviorSubject<Parameters | undefined> = new BehaviorSubject<Parameters | undefined>(undefined);
  public get parameters(): Observable<Parameters | undefined> {
    return this._parameters.asObservable();
  }

  constructor(id: string, createOptions: ComponentContextCreateOptionsBasf) {
    this.id = id;
    this.options = createOptions;

    this.dataflowIntegration = {
      canIntegrate: true, // Указываем, что компонент интегрируется к Dataflow сервису (и может управляться с помощью параметров отчёта)
      integrated: false,
    };

    this.readyState = ComponentContextReadyState.Loading;

    this._initDefaultMark();

    this._parameters
      .pipe(debounceTime(300))
      .subscribe((parameters) => {
        if (parameters) {
          this.query(parameters);
        }
      });
  }

  private async _initDefaultMark() {
    
    const { dataflowService } = this.options;
    const methodParam = dataflowService.getParameter("@method") as SingleValueParameter;
    const commoditiesParam = dataflowService.getParameter("@commodities") as MultiValueParameter;
    const baseIndexParameter = dataflowService.getParameter("@base_index") as SingleValueParameter; 8
    const calendarParam = dataflowService.getParameter("@calendar") as MultiValueParameter;
    const calendarDisplayParameter = dataflowService.getParameter("@calendar_display") as MultiValueParameter;
    
    const { serviceProvider } = this.options;
    const dictionariesService = serviceProvider.resolve<DictionariesService>(ServicesRegister.Dictionary);
    const {calendar, calendarDisplay} = await getDefaultMark(dictionariesService);
    calendarParam.value = calendar;
    calendarDisplayParameter.value = calendarDisplay;

    this._parameters.next({
      method: methodParam.value as number,
      commodities: commoditiesParam.value as number[],
      calendar: calendarParam.value as number[],
      baseIndex: baseIndexParameter.value as number,
    });
  }

  async update(command: UpdateCommand): Promise<void> {
    const calendar = command.values.get("@calendar") as number[];
    const baseIndex = command.values.get("@base_index") as number;
    const commodities = command.values.get("@commodities") as number[];
    const method = command.values.get("@method") as number;
    const parameters = clone(this._parameters.value);
    if (parameters !== undefined) {
      if (calendar !== undefined) {
        parameters.calendar = calendar;
      }
      if (commodities !== undefined) {
        parameters.commodities = commodities;
      }
      if (method !== undefined) {
        parameters.method = method;
      }
      if (baseIndex !== undefined) {
        parameters.baseIndex = baseIndex;
      }

      if (!equal(parameters, this._parameters.value)) {
        this._parameters.next(parameters);
      }
    }
  }

  async query(parameters: Parameters) {
    const { calendar, commodities, baseIndex, method } = parameters;

    this.readyState = ComponentContextReadyState.Loading;
    this.olapResult$.next(null);
    this.sources$.next(null);

    const { dataSourcesService } = this.options;
    const proxySources = {
      1028: 'Crude Oil',
      20003: 'Gold',
      2002: 'DAX & S&P 500',
      2001: 'DAX & S&P 500',
    }
    const sources = await Promise.all(commodities.map(commodity => dataSourcesService.getDataSources(SLIDE_NUMBER, 1, Number(commodity)).then(source => {
      const proxyPrefix = proxySources[commodity];
      return Boolean(proxyPrefix) ? `${proxyPrefix}: ${source}` : source;
    })));
    this.sources$.next(`Field Crops: ${Array.from(new Set(sources.join(', ').split(', ')).values()).join(', ')}`);

    const cubeSlideDef = await dataService.getCubeDefinition("slide_20");
    const cubeParameters = clone(cubeSlideDef.parameters);
    cubeParameters["p_method"]!["defaultValue"] = method;
    cubeParameters["p_calendar"]!["defaultValue"] = calendar.join(',');
    cubeParameters["p_baseIndex"]!["defaultValue"] = baseIndex;
    const cubeSlide = await dataService.getCube({
      id: cubeSlideDef.id,
      parameters: cubeParameters,
    });

    const olapQuery = ({
      columns: {
        showEmpty: false,
        parts: [
          {
            path: "calendar.default",
            selection: calendar,
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          { path: "commodities.default", selection: commodities, selectionMode: SelectionMode.Include },
          {
            path: "Measures",
            measures: [
              { id: "value", aggregationType: AggregationType.None },
            ],
          },
        ],
      },
      sourceResolveOptions: {
        sourceId: cubeSlide.id,
      },
    } as any);

    const olapResult = await cubeSlide.query(olapQuery);
    this.readyState = ComponentContextReadyState.Loaded;
    this.olapResult$.next(olapResult);
  }
}
